import { fontFor } from '../format'
import type { SimBlock } from '../spec/types'

/**
 * A dry run: the inputs on the left, the resolved outcome and the layer stack
 * it came from on the right.
 *
 * Verbatim from `docs/design/reference/blocks/sim.html`. Two panes separated by
 * a 1px gap over `--k-line`, so the rule between them is the container's own
 * background rather than a border either pane owns.
 *
 * The corpus uses this shape for three jobs that are the same job — replaying a
 * `decision_ref`, running `Match()` against a chosen catalog version, and
 * showing the evidence behind a promotion suggestion — and the discipline is
 * identical in all three:
 *
 *   - **Nothing here writes.** The inputs are read-only because the pane is a
 *     record of what was read, not a form that could quietly become the source
 *     of it. A replay that let an operator edit the stored inputs would destroy
 *     the property invariant 9 exists to guarantee: given only the reference,
 *     the outcome reconstructs six months later without guessing.
 *   - **Every layer is listed, including the ones that never ran.** Layers are
 *     evaluated in order and stop at the first hit; the rest are shown dimmed
 *     via `data-dim` rather than dropped. "Not evaluated" is the answer to "why
 *     didn't rule 4 fire", and hiding it turns a complete explanation into a
 *     partial one.
 *   - **A confidence tier is a tone, not a verdict.** A low-confidence layer
 *     renders as evidence beside the outcome and never as the outcome — the
 *     suggestion tier auto-applies nowhere (invariant 11).
 */
export function Sim({ block }: { block: SimBlock }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(260px,1fr) 1.4fr',
        gap: '1px',
        background: 'var(--k-line)',
        border: '1px solid var(--k-line)',
      }}
    >
      <div style={{ background: 'var(--k-panel)', padding: '12px' }}>
        <div
          style={{
            fontSize: '10.5px',
            letterSpacing: '.07em',
            textTransform: 'uppercase',
            color: 'var(--k-mut2)',
            marginBottom: '8px',
          }}
        >
          {block.inputTitle}
        </div>

        {block.fields.map((f) => (
          <div key={f[0]} style={{ marginBottom: '8px' }}>
            <div
              style={{
                fontSize: '10.5px',
                color: 'var(--k-mut)',
                marginBottom: '3px',
                fontFamily: 'var(--k-mono)',
              }}
            >
              {f[0]}
            </div>
            {/* Read-only, as in the prototype, whose change handler is a no-op.
                The visible key above is not programmatically associated with
                the field, so the label is repeated for a screen reader. */}
            <input
              value={f[1]}
              readOnly
              aria-label={f[0]}
              style={{
                height: '27px',
                width: '100%',
                border: '1px solid var(--k-line)',
                background: 'var(--k-bg)',
                color: 'var(--k-ink)',
                fontFamily: fontFor(f[2]),
                fontSize: '12px',
                padding: '0 7px',
                borderRadius: 0,
              }}
            />
          </div>
        ))}

        <button
          type="button"
          style={{
            height: '29px',
            padding: '0 13px',
            marginTop: '4px',
            border: 0,
            background: 'var(--k-accent)',
            color: 'var(--k-accentink)',
            font: 'inherit',
            fontWeight: 800,
            fontSize: '12px',
            cursor: 'pointer',
            width: '100%',
            textAlign: 'left',
          }}
        >
          {block.runLabel}
        </button>

        <div style={{ fontSize: '11px', color: 'var(--k-mut2)', marginTop: '6px' }}>
          {block.inputNote}
        </div>
      </div>

      <div style={{ background: 'var(--k-panel)', padding: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '9px' }}>
          <span
            style={{
              fontSize: '10.5px',
              letterSpacing: '.07em',
              textTransform: 'uppercase',
              color: 'var(--k-mut2)',
            }}
          >
            {block.outTitle}
          </span>
          <span
            data-tone={block.outTone}
            style={{
              padding: '1px 6px',
              fontSize: '10px',
              fontWeight: 800,
              letterSpacing: '.05em',
              textTransform: 'uppercase',
            }}
          >
            {block.outcome}
          </span>
          <span
            style={{
              marginLeft: 'auto',
              fontFamily: 'var(--k-mono)',
              fontSize: '11px',
              color: 'var(--k-mut)',
            }}
          >
            {block.outRef}
          </span>
        </div>

        {block.layers.map((l, i) => (
          <div
            // Layers are positional in the design corpus; the evaluation order
            // is the content and there is no layer id to key on.
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            data-dim={l[6] === true ? '1' : ''}
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr auto',
              alignItems: 'center',
              gap: '10px',
              padding: '6px 0',
              borderBottom: '1px solid var(--k-line)',
            }}
          >
            <span
              data-tone={l[1]}
              style={{
                padding: '0 5px',
                fontSize: '9.5px',
                fontWeight: 800,
                letterSpacing: '.05em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
              }}
            >
              {l[0]}
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '12px', fontWeight: 600 }}>{l[2]}</div>
              <div
                style={{
                  fontFamily: 'var(--k-mono)',
                  fontSize: '11px',
                  color: 'var(--k-mut)',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                }}
              >
                {l[3]}
              </div>
            </div>
            <span
              data-tone={l[5] ?? 'n'}
              style={{
                padding: '0 5px',
                fontSize: '10px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {l[4]}
            </span>
          </div>
        ))}

        <div
          style={{
            marginTop: '10px',
            padding: '9px 10px',
            background: 'var(--k-bg)',
            border: '1px solid var(--k-line)',
            fontSize: '11.5px',
            color: 'var(--k-mut)',
            textWrap: 'pretty',
          }}
        >
          {block.outNote}
        </div>
      </div>
    </div>
  )
}
